import {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  memo,
} from 'react';
import { Search, ChevronUp, ChevronDown, LogIn } from 'lucide-react';
import {
  Input,
  Alert,
  AlertDescription,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@roger/ui-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  getAdoptionReportByBranch,
  getBranchNames,
} from '../../services/rogerDocApi';
import {
  type BranchReportEntry,
  processBranchReportData,
  getTotalEmrFilled,
  getTotalExternalCompletion,
  getPendingVisits,
  getRogerVsExternalPercentage,
} from '../../types/adoptionReport.types';
import {
  getPendingVisits as getVisitPendingVisits,
  getRogerVsExternalPercentage as getVisitRogerVsExternalPercentage,
} from '../../types/visitCompletion.types';
import { getClinicianFullName } from '../../types/clinician.types';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import PageLoader from '../PageLoader';

export interface VisitCompletionByClinicianAndBranchRef {
  exportToCSV: () => void;
}

export interface VisitCompletionByClinicianAndBranchProps {
  onExportRegistry?: (exportFunc: (() => void) | null) => void;
}

// Column widths to match JS version roughly but using Tailwind classes/px
// JS: ['250px', '150px', '250px', '220px', '220px', '250px', '220px']
const COLUMN_WIDTHS = [
  '250px',
  '150px',
  '250px',
  '220px',
  '220px',
  '250px',
  '220px',
];
const TOTAL_WIDTH = COLUMN_WIDTHS.reduce((sum, w) => sum + parseInt(w), 0);

// Branch Selector Component
interface BranchSelectorProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}

const BranchSelector = ({ options, value, onChange }: BranchSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const parentRef = useRef<HTMLDivElement>(null);

  // Filter options
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(opt =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const allSelected = options.length > 0 && value.length === options.length;

  const handleAllChange = (_checked: boolean) => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  };

  const handleOptionChange = (id: string, checked: boolean) => {
    if (checked) {
      onChange([...value, id]);
    } else {
      onChange(value.filter(v => v !== id));
    }
  };

  const label = allSelected
    ? 'All branches selected'
    : value.length === 0
      ? 'Select branches'
      : `${value.length} branches selected`;

  // Virtualizer for the branch list
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32, // Estimate height of checkbox row
    overscan: 5,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-[250px] justify-between font-normal'
        >
          <span className='truncate'>{label}</span>
          <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-2' align='start'>
        <div className='relative mb-2'>
          <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground opacity-50' />
          <Input
            className='pl-8 h-9'
            placeholder='Search...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className='px-2 py-1.5 mb-1'>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='select-all-branches'
              checked={allSelected}
              onCheckedChange={checked => handleAllChange(!!checked)}
            />
            <label
              htmlFor='select-all-branches'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
            >
              All branches
            </label>
          </div>
        </div>

        <div ref={parentRef} className='h-[200px] overflow-y-auto w-full'>
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {filteredOptions.length > 0 ? (
              virtualizer.getVirtualItems().map(virtualItem => {
                const opt = filteredOptions[virtualItem.index];
                const isSelected = value.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    className='flex items-center space-x-2 px-2 py-1.5'
                  >
                    <Checkbox
                      id={`branch-${opt.value}`}
                      checked={isSelected}
                      onCheckedChange={checked =>
                        handleOptionChange(opt.value, !!checked)
                      }
                    />
                    <label
                      htmlFor={`branch-${opt.value}`}
                      className='text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer truncate'
                      title={opt.label}
                    >
                      {opt.label}
                    </label>
                  </div>
                );
              })
            ) : (
              <div className='py-6 text-center text-sm text-muted-foreground'>
                No branches found
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Sortable header component
const SortableHeader = ({
  column,
  children,
  width,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: string;
  children: React.ReactNode;
  width: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
}) => (
  <th
    className='cursor-pointer select-none bg-muted border-b-2 border-border p-3 text-left font-semibold text-sm sticky top-0 z-10 hover:bg-muted/80 transition-colors'
    style={{ width, minWidth: width, maxWidth: width }}
    onClick={() => onSort(column)}
  >
    <div className='flex items-center justify-between pointer-events-none'>
      <span className='font-semibold'>{children}</span>
      {sortColumn === column && sortDirection === 'asc' ? (
        <ChevronUp className='h-4 w-4' />
      ) : sortColumn === column && sortDirection === 'desc' ? (
        <ChevronDown className='h-4 w-4' />
      ) : (
        <ChevronDown className='h-4 w-4 opacity-30' />
      )}
    </div>
  </th>
);

// Virtual Row Component
const VirtualRow = memo(
  ({
    entry,
    showBreakdown,
  }: {
    entry: BranchReportEntry;
    showBreakdown: boolean;
  }) => {
    const totalPercentage = getRogerVsExternalPercentage(entry) + '%';

    return (
      <>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[0],
            minWidth: COLUMN_WIDTHS[0],
            maxWidth: COLUMN_WIDTHS[0],
          }}
        >
          <div className='flex items-center justify-between gap-2 overflow-hidden'>
            <span
              className='font-medium truncate'
              title={getClinicianFullName(entry.clinician)}
            >
              {getClinicianFullName(entry.clinician)}
            </span>
            {!entry.clinician.hasLoggedIn && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='shrink-0'>
                      <LogIn className='h-4 w-4 text-destructive cursor-help' />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This user has never logged into the app</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[1],
            minWidth: COLUMN_WIDTHS[1],
            maxWidth: COLUMN_WIDTHS[1],
          }}
        >
          <span className='text-sm truncate block'>
            {entry.clinician.title || '-'}
          </span>
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[2],
            minWidth: COLUMN_WIDTHS[2],
            maxWidth: COLUMN_WIDTHS[2],
          }}
        >
          <span
            className='text-sm font-medium truncate block'
            title={entry.branchName}
          >
            {entry.branchName}
          </span>
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[3],
            minWidth: COLUMN_WIDTHS[3],
            maxWidth: COLUMN_WIDTHS[3],
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{getPendingVisits(entry)}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{getVisitPendingVisits(entry.oasis)} OASIS</div>
                <div>{getVisitPendingVisits(entry.regular)} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{getPendingVisits(entry)}</span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[4],
            minWidth: COLUMN_WIDTHS[4],
            maxWidth: COLUMN_WIDTHS[4],
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{getTotalEmrFilled(entry)}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{entry.oasis.emrFilled} OASIS</div>
                <div>{entry.regular.emrFilled} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{getTotalEmrFilled(entry)}</span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[5],
            minWidth: COLUMN_WIDTHS[5],
            maxWidth: COLUMN_WIDTHS[5],
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>
                {getTotalExternalCompletion(entry)}
              </span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{entry.oasis.externalCompletion} OASIS</div>
                <div>{entry.regular.externalCompletion} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>
              {getTotalExternalCompletion(entry)}
            </span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS[6],
            minWidth: COLUMN_WIDTHS[6],
            maxWidth: COLUMN_WIDTHS[6],
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{totalPercentage}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>
                  {getVisitRogerVsExternalPercentage(entry.oasis)}% OASIS
                </div>
                <div>
                  {getVisitRogerVsExternalPercentage(entry.regular)}% Regular
                </div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{totalPercentage}</span>
          )}
        </td>
      </>
    );
  }
);
VirtualRow.displayName = 'VirtualRow';

const VisitCompletionByClinicianAndBranch = forwardRef<
  VisitCompletionByClinicianAndBranchRef,
  VisitCompletionByClinicianAndBranchProps
>(({ onExportRegistry }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('rogerPercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [reportData, setReportData] = useState<BranchReportEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(() => {
    const saved = localStorage.getItem('adoptionReportBranch_showBreakdown');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideNeverLoggedIn, setHideNeverLoggedIn] = useState(() => {
    const saved = localStorage.getItem(
      'adoptionReportBranch_hideNeverLoggedIn'
    );
    return saved ? JSON.parse(saved) : false;
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [branchOptions, setBranchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);

  // Ref for virtualized list container
  const parentRef = useRef<HTMLDivElement>(null);

  // Ref to track the latest request ID to handle race conditions
  const requestIdRef = useRef(0);

  const { showError } = useStore(
    useShallow(state => ({
      showError: state.showError,
    }))
  );

  // Fetch branch names on mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setBranchesLoading(true);
        const data = await getBranchNames();

        const options = Object.entries(data).map(([id, name]) => ({
          value: id,
          label: name,
        }));

        options.sort((a, b) => a.label.localeCompare(b.label));

        setBranchOptions(options);
        // Select all by default
        setSelectedBranches(options.map(o => o.value));
      } catch (err) {
        console.error('Error loading branches:', err);
        showError('Failed to load branch list');
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchBranches();
  }, [showError]);

  // Fetch report data
  useEffect(() => {
    if (branchesLoading) return;

    // If no branches selected, clear data
    if (selectedBranches.length === 0) {
      setReportData([]);
      return;
    }

    const fetchReportData = async () => {
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        setError(null);

        const data = await getAdoptionReportByBranch(
          selectedBranches,
          fromDate,
          toDate
        );

        // Only update state if this is the latest request
        if (currentRequestId === requestIdRef.current) {
          const processedData = processBranchReportData(data);
          setReportData(processedData);
          console.log(
            'Adoption report by branch data loaded, entries:',
            processedData.length
          );
        }
      } catch (err) {
        // Only handle error if this is the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error('Error loading adoption report by branch:', err);
          const errorMessage =
            err instanceof Error ? err.message : 'Unknown error';
          setError(errorMessage);
          setReportData([]);
        }
      } finally {
        // Only turn off loading if this is the latest request
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    // Debounce to prevent double calls
    const timeoutId = setTimeout(fetchReportData, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedBranches, fromDate, toDate, branchesLoading]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // Default to desc for numeric columns, asc for text
      if (
        ['pending', 'roger', 'external', 'rogerPercentage'].includes(column)
      ) {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...reportData];

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => {
        const fullName = getClinicianFullName(entry.clinician).toLowerCase();
        const firstName = entry.clinician.firstName.toLowerCase();
        const lastName = entry.clinician.lastName.toLowerCase();
        const title = (entry.clinician.title || '').toLowerCase();
        const branch = entry.branchName.toLowerCase();

        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          title.includes(searchLower) ||
          branch.includes(searchLower)
        );
      });
    }

    // Apply hasLoggedIn filter
    if (hideNeverLoggedIn) {
      filtered = filtered.filter(entry => entry.clinician.hasLoggedIn);
    }

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'name':
            aValue =
              `${a.clinician.lastName}, ${a.clinician.firstName}`.toLowerCase();
            bValue =
              `${b.clinician.lastName}, ${b.clinician.firstName}`.toLowerCase();
            break;
          case 'title':
            aValue = (a.clinician.title || '').toLowerCase();
            bValue = (b.clinician.title || '').toLowerCase();
            break;
          case 'branch':
            aValue = a.branchName.toLowerCase();
            bValue = b.branchName.toLowerCase();
            break;
          case 'pending':
            aValue = getPendingVisits(a);
            bValue = getPendingVisits(b);
            break;
          case 'roger':
            aValue = getTotalEmrFilled(a);
            bValue = getTotalEmrFilled(b);
            break;
          case 'external':
            aValue = getTotalExternalCompletion(a);
            bValue = getTotalExternalCompletion(b);
            break;
          case 'rogerPercentage': {
            const aTotal = getTotalEmrFilled(a) + getTotalExternalCompletion(a);
            aValue = aTotal === 0 ? 0 : getTotalEmrFilled(a) / aTotal;

            const bTotal = getTotalEmrFilled(b) + getTotalExternalCompletion(b);
            bValue = bTotal === 0 ? 0 : getTotalEmrFilled(b) / bTotal;
            break;
          }
          default:
            return 0;
        }

        if (typeof aValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        } else {
          return sortDirection === 'asc'
            ? aValue - (bValue as number)
            : (bValue as number) - aValue;
        }
      });
    } else {
      // Default sort (should not be hit given initialization)
      filtered.sort((a, b) => {
        const nameCompare = a.clinician.username.localeCompare(
          b.clinician.username
        );
        if (nameCompare !== 0) return nameCompare;
        return a.branchName.localeCompare(b.branchName);
      });
    }

    return filtered;
  }, [reportData, searchTerm, sortColumn, sortDirection, hideNeverLoggedIn]);

  // Virtualizer for the main table
  const virtualizer = useVirtualizer({
    count: filteredAndSortedData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Estimated height of each row
    overscan: 5,
  });

  // Export to CSV function
  const exportToCSV = () => {
    const escapeCsvValue = (value: unknown) => {
      if (value == null) return '';
      const stringValue = String(value);
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      'Clinician Name (Last, First)',
      'Title',
      'Branch',
      'Has Logged In',
      'Pending Visits',
      'Pending Visits - OASIS',
      'Pending Visits - Regular',
      'Completed in Roger',
      'Completed in Roger - OASIS',
      'Completed in Roger - Regular',
      'Completed Outside of Roger',
      'Completed Outside of Roger - OASIS',
      'Completed Outside of Roger - Regular',
      '% Completed in Roger',
      '% Completed in Roger - OASIS',
      '% Completed in Roger - Regular',
    ];

    const csvRows = reportData.map(entry => {
      const totalPercentage = getRogerVsExternalPercentage(entry) + '%';

      return [
        escapeCsvValue(getClinicianFullName(entry.clinician)),
        escapeCsvValue(entry.clinician.title || ''),
        escapeCsvValue(entry.branchName),
        escapeCsvValue(entry.clinician.hasLoggedIn ? 'Yes' : 'No'),
        escapeCsvValue(getPendingVisits(entry)),
        escapeCsvValue(getVisitPendingVisits(entry.oasis)),
        escapeCsvValue(getVisitPendingVisits(entry.regular)),
        escapeCsvValue(getTotalEmrFilled(entry)),
        escapeCsvValue(entry.oasis.emrFilled),
        escapeCsvValue(entry.regular.emrFilled),
        escapeCsvValue(getTotalExternalCompletion(entry)),
        escapeCsvValue(entry.oasis.externalCompletion),
        escapeCsvValue(entry.regular.externalCompletion),
        escapeCsvValue(totalPercentage),
        escapeCsvValue(getVisitRogerVsExternalPercentage(entry.oasis) + '%'),
        escapeCsvValue(getVisitRogerVsExternalPercentage(entry.regular) + '%'),
      ].join(',');
    });

    const csvContent = [headers.map(escapeCsvValue).join(','), ...csvRows].join(
      '\n'
    );

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `roger_adoption_report_by_branch_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Expose exportToCSV method via ref
  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  // Register export function
  useEffect(() => {
    if (onExportRegistry) {
      if (loading || reportData.length === 0) {
        onExportRegistry(null);
      } else {
        onExportRegistry(exportToCSV);
      }
    }
  }, [loading, reportData.length, onExportRegistry]);

  if (loading) {
    return <PageLoader message='Loading adoption report data...' />;
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Date info and Branch Selector */}
      <div className='flex gap-4 items-center mb-4 mt-[-8px]'>
        <BranchSelector
          options={branchOptions}
          value={selectedBranches}
          onChange={setSelectedBranches}
        />
        <div className='text-sm text-[#735bd6]'>
          All data is from 12/02/2025 onward
        </div>
      </div>

      {/* Search Bar and Controls */}
      <div className='mb-4'>
        <div className='flex gap-4 items-center flex-wrap'>
          <div className='relative w-[250px]'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder='Search by clinician name...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-9'
            />
          </div>

          <div className='flex gap-2 items-center'>
            <Input
              type='date'
              value={fromDate ? fromDate.toISOString().split('T')[0] : ''}
              onChange={e => {
                setFromDate(e.target.value ? new Date(e.target.value) : null);
              }}
              className='w-[150px]'
              placeholder='From date'
            />
            <Input
              type='date'
              value={toDate ? toDate.toISOString().split('T')[0] : ''}
              onChange={e => {
                setToDate(e.target.value ? new Date(e.target.value) : null);
              }}
              className='w-[150px]'
              placeholder='To date'
            />
          </div>

          <div className='flex items-center gap-4 ml-2'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='show-breakdown'
                checked={showBreakdown}
                onCheckedChange={checked => {
                  setShowBreakdown(!!checked);
                  localStorage.setItem(
                    'adoptionReportBranch_showBreakdown',
                    JSON.stringify(!!checked)
                  );
                }}
              />
              <label
                htmlFor='show-breakdown'
                className='text-sm cursor-pointer'
              >
                Visit Type breakdown
              </label>
            </div>

            <div className='flex items-center space-x-2'>
              <Checkbox
                id='hide-never-logged-in'
                checked={hideNeverLoggedIn}
                onCheckedChange={checked => {
                  setHideNeverLoggedIn(!!checked);
                  localStorage.setItem(
                    'adoptionReportBranch_hideNeverLoggedIn',
                    JSON.stringify(!!checked)
                  );
                }}
              />
              <label
                htmlFor='hide-never-logged-in'
                className='text-sm cursor-pointer'
              >
                Hide users who never logged in
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant='destructive' className='mb-4'>
          <AlertDescription>
            Error loading adoption report: {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className='flex-1 overflow-hidden min-h-0'>
        {branchesLoading ? (
          <PageLoader
            message={
              branchesLoading
                ? 'Loading branches...'
                : 'Loading adoption report data...'
            }
          />
        ) : selectedBranches.length === 0 ? (
          <div className='flex items-center justify-center h-[200px]'>
            <div className='text-lg font-medium text-muted-foreground'>
              Please select one or more branches to view the report
            </div>
          </div>
        ) : (
          <div ref={parentRef} className='h-full overflow-auto pb-[50px]'>
            <div style={{ width: '100%', minWidth: `${TOTAL_WIDTH}px` }}>
              <table className='w-full border-collapse bg-background table-fixed'>
                <thead className='sticky top-0 z-10 bg-muted/50 shadow-sm'>
                  <tr>
                    <SortableHeader
                      column='name'
                      width={COLUMN_WIDTHS[0]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Clinician Name
                    </SortableHeader>
                    <SortableHeader
                      column='title'
                      width={COLUMN_WIDTHS[1]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Title
                    </SortableHeader>
                    <SortableHeader
                      column='branch'
                      width={COLUMN_WIDTHS[2]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Branch
                    </SortableHeader>
                    <SortableHeader
                      column='pending'
                      width={COLUMN_WIDTHS[3]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Pending Visits
                    </SortableHeader>
                    <SortableHeader
                      column='roger'
                      width={COLUMN_WIDTHS[4]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Completed in Roger
                    </SortableHeader>
                    <SortableHeader
                      column='external'
                      width={COLUMN_WIDTHS[5]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Completed Outside of Roger
                    </SortableHeader>
                    <SortableHeader
                      column='rogerPercentage'
                      width={COLUMN_WIDTHS[6]}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      % Completed in Roger
                    </SortableHeader>
                  </tr>
                </thead>

                <tbody>
                  <tr style={{ height: `${virtualizer.getTotalSize()}px` }}>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {virtualizer.getVirtualItems().map(virtualItem => {
                          const entry =
                            filteredAndSortedData[virtualItem.index];
                          return (
                            <div
                              key={virtualItem.key}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                              }}
                            >
                              <table className='w-full border-collapse table-fixed'>
                                <tbody>
                                  <tr
                                    className={
                                      virtualItem.index % 2 === 0
                                        ? 'bg-muted/30'
                                        : 'bg-background'
                                    }
                                  >
                                    <VirtualRow
                                      entry={entry}
                                      showBreakdown={showBreakdown}
                                    />
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

VisitCompletionByClinicianAndBranch.displayName =
  'VisitCompletionByClinicianAndBranch';

export default VisitCompletionByClinicianAndBranch;
